// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Point, Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { TerceraChartCashItemSeriesDataType } from '../Series/TerceraChartCashItemSeriesEnums';
import { SolidBrush } from '@shared/commons/Graphics';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { TerceraChartAction, TerceraChartActionEnum } from '../TerceraChartAction';
import { TerceraChartPriceScaleRendererMouseState } from '../Renderers/Scales/TerceraChartPriceScaleRenderer';
import { BrowserUtils } from '@shared/utils/BrowserUtils';
import { Resources } from '@shared/localizations/Resources';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { DynProperty, PairColor } from '@shared/commons/DynProperty';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { Periods } from '@shared/utils/History/TFInfo';
import { InfoWindowDockingType } from './InfoWindow/InfoWindowDockingType';
import { InfoWindowDescriptionItem } from './InfoWindow/InfoWindowDescriptionItem';
import { InfoWindowItemType } from './InfoWindow/InfoWindowItemType';
import { InfoWindowShowingType } from './InfoWindow/InfoWindowShowingType';
import { getInfoWindowDisplayModeKey, InfoWindowDisplayMode } from './InfoWindow/InfoWindowDisplayMode';
import { BarIndex } from '@shared/commons/cache/History/HistoryConstants';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { StringUtils } from '@shared/utils/StringUtils';
import { LayersEnum } from '../Utils/LayersEnum';
import { VolumeFormatter } from '@shared/utils/Volume/VolumeFormatter';
import { createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
export class TerceraChartInfoWindowRenderer extends TerceraChartBaseRenderer {
    constructor(chart, chartWindow) {
        super(chart);
        this.forceInfoWindowShowingType = null; // Для установки особого типа - например для Portfolio growth
        this.displayMode = InfoWindowDisplayMode.SeparateWindow;
        this.cursorRectIndent = 15;
        this.edgeMargin = 8;
        this.leftIndent = 8;
        this.rightIndent = 8;
        // #region Rectangles, width, mouse
        // Прямоугольник, который содержит координаты окна, в которых оно отображается
        this.LastBounds = new Rectangle();
        this.curLabelWidthMax = 0;
        this.curValueWidthMax = 0;
        this.curTotalWidth = 0;
        this.undockColumnsNumber = 2; // when undock
        // Определяет находится ли сейчас мышка в окне
        this.IsMouseDownOut = false;
        this.lastMouseMoveLocation = new Point();
        this.lastSelectedBarIndex = null;
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.mouseLeave = false;
        // #endregion
        // #region Font, Color, Images, ...
        this.FFontBold = null;
        this.rowHeight = 0;
        this.FCurrentBrush = null;
        this.FCurrentForeBrush = null;
        // #endregion
        this.lastDrawCount = 0;
        this.assignLayer = LayersEnum.CrossHair;
        this.browserOffset = BrowserUtils.isEdgeOrIEBrowser ? 1 : 3;
        this.shortMode = false;
        this.FFont = null;
        this.FBackColor = null;
        this.FForeColor = null;
        this.FNeutralValueColor = null;
        this.FIncreaseValueColor = null;
        this.FDecreaseValueColor = null;
        this.instrument = null;
        this.account = null;
        this.window = chartWindow;
        // #region InfoWindowShowingType
        // Список всех возхможных item'ов для отображения
        const dict = {};
        dict[InfoWindowItemType.Bars] = new InfoWindowDescriptionItem(InfoWindowItemType.Bars);
        dict[InfoWindowItemType.Date] = new InfoWindowDescriptionItem(InfoWindowItemType.Date);
        dict[InfoWindowItemType.Time] = new InfoWindowDescriptionItem(InfoWindowItemType.Time);
        dict[InfoWindowItemType.Open] = new InfoWindowDescriptionItem(InfoWindowItemType.Open);
        dict[InfoWindowItemType.Close] = new InfoWindowDescriptionItem(InfoWindowItemType.Close);
        dict[InfoWindowItemType.High] = new InfoWindowDescriptionItem(InfoWindowItemType.High);
        dict[InfoWindowItemType.Low] = new InfoWindowDescriptionItem(InfoWindowItemType.Low);
        dict[InfoWindowItemType.HighLow] = new InfoWindowDescriptionItem(InfoWindowItemType.HighLow);
        dict[InfoWindowItemType.Change] = new InfoWindowDescriptionItem(InfoWindowItemType.Change);
        dict[InfoWindowItemType.DailyChange] = new InfoWindowDescriptionItem(InfoWindowItemType.DailyChange);
        dict[InfoWindowItemType.Ticks] = new InfoWindowDescriptionItem(InfoWindowItemType.Ticks);
        dict[InfoWindowItemType.Volume] = new InfoWindowDescriptionItem(InfoWindowItemType.Volume);
        dict[InfoWindowItemType.Ask] = new InfoWindowDescriptionItem(InfoWindowItemType.Ask);
        dict[InfoWindowItemType.Bid] = new InfoWindowDescriptionItem(InfoWindowItemType.Bid);
        dict[InfoWindowItemType.PortfolioGrowth] = new InfoWindowDescriptionItem(InfoWindowItemType.PortfolioGrowth);
        dict[InfoWindowItemType.Funding] = new InfoWindowDescriptionItem(InfoWindowItemType.Funding);
        dict[InfoWindowItemType.Countdown] = new InfoWindowDescriptionItem(InfoWindowItemType.Countdown);
        this.DictItems = dict;
        for (const key in dict) {
            const item = dict[key];
            const locKey = 'chart.infoWindow.Items.' + TerceraChartInfoWindowRenderer.infoWindowItemTypeToString(key);
            item.Allowed = !Resources.isHidden(locKey);
            item.Visible = !Resources.isHidden(locKey + '.VisibilityByDefault');
        }
        // Последний использорванный тип отображения item'ов
        this.lastInfoWindowShowingType = InfoWindowShowingType.IntradayAbsoluteChart;
        // #endregion
        this.DockingType = InfoWindowDockingType.Top;
        this.Font = ThemeManager.Fonts.Font_12F_regular.copy();
        this.Localize();
        this.ThemeChanged();
        this.SetClassName('TerceraChartInfoWindowRenderer');
    }
    IsNeedForceUpdate() {
        const item = this.DictItems[InfoWindowItemType.Countdown];
        if (item != null) {
            return item.Visible;
        }
        return false;
    }
    get GetUndockedWidth() {
        return (this.curValueWidthMax + this.curLabelWidthMax) * this.undockColumnsNumber;
    }
    get IsDocked() {
        return this.DockingType !== InfoWindowDockingType.None;
    }
    get IsDockedTop() {
        return this.DockingType === InfoWindowDockingType.Top;
    }
    get IsDockedBottom() {
        return this.DockingType === InfoWindowDockingType.Bottom;
    }
    get Font() {
        return this.FFont;
    }
    set Font(value) {
        this.FFont = value;
        this.FFontBold = value.copy();
        this.FFontBold.FontWeight = 'bold';
        const DictItems = this.DictItems;
        const keys = Object.keys(DictItems);
        const len = keys.length;
        for (let i = 0; i < len; i++) {
            DictItems[keys[i]].Font = this.FFont;
        }
        this.rowHeight = this.FFont.Height + 2 + this.edgeMargin * 2;
    }
    get BackColor() {
        return this.FBackColor;
    }
    set BackColor(value) {
        this.FBackColor = value;
        this.FCurrentBrush = new SolidBrush(value);
    }
    get ForeColor() {
        return this.FForeColor;
    }
    set ForeColor(value) {
        this.FForeColor = value;
        this.FCurrentForeBrush = new SolidBrush(value);
    }
    get NeutralValueColor() {
        return this.FNeutralValueColor;
    }
    set NeutralValueColor(value) {
        this.FNeutralValueColor = value;
        this.FNeutralValueBrush = new SolidBrush(value);
    }
    get IncreaseValueColor() {
        return this.FIncreaseValueColor;
    }
    set IncreaseValueColor(value) {
        this.FIncreaseValueColor = value;
        this.FIncreaseValueBrush = new SolidBrush(value);
    }
    get DecreaseValueColor() {
        return this.FDecreaseValueColor;
    }
    set DecreaseValueColor(value) {
        this.FDecreaseValueColor = value;
        this.FDecreaseValueBrush = new SolidBrush(value);
    }
    get Instrument() {
        return this.instrument;
    }
    set Instrument(value) {
        this.instrument = value;
        this.UpdateText(-1, null);
    }
    get Account() {
        return this.account;
    }
    set Account(value) {
        this.account = value;
        this.UpdateText(-1, null);
    }
    get ShortMode() {
        return this.shortMode;
    }
    set ShortMode(value) {
        this.shortMode = value;
        this.Localize();
    }
    get CorrectRectangle() {
        const cr = this.window.ClientRectangle;
        const ww = this.window;
        const correctRectangle = new Rectangle();
        correctRectangle.X = cr.X;
        correctRectangle.Y = ww.HeaderVisible ? cr.Y + ww.HeaderHeight : cr.Y;
        correctRectangle.Width = cr.Width;
        correctRectangle.Height = ww.HeaderVisible ? cr.Height - ww.HeaderHeight : cr.Height;
        return correctRectangle;
    }
    // Draw items in two equal columns
    Draw(gr, window, windowsContainer, advParams) {
        if (this.isHide() || isNullOrUndefined(window)) {
            return;
        }
        const param = advParams;
        const cashItemSeries = param.mainPriceRenderer.Series;
        if (!cashItemSeries) {
            return;
        }
        const LastBounds = this.LastBounds;
        const FCurrentBrush = this.FCurrentBrush;
        this.lastInfoWindowShowingType =
            cashItemSeries.CashItem.FPeriod <= 0 && cashItemSeries.CashItem.HistoryType === HistoryType.BID
                ? InfoWindowShowingType.TicksAbsoluteChart
                : InfoWindowShowingType.IntradayAbsoluteChart;
        if (this.forceInfoWindowShowingType) {
            this.lastInfoWindowShowingType = this.forceInfoWindowShowingType;
        }
        let barIndex = 0;
        if (cashItemSeries.CashItem.isRealTimeBuilding) {
            barIndex = cashItemSeries.Count() - cashItemSeries.rightPaddingBarsCount - 1;
        }
        else {
            barIndex = Math.round(window.i1);
        }
        if (!this.mouseLeave) {
            if (this.IsMouseDownOut) {
                barIndex = this.lastSelectedBarIndex + cashItemSeries.leftPaddingBarsCount;
            }
            else {
                this.lastSelectedBarIndex = Math.floor(window.PointsConverter.GetDataIndexByX(this.lastMouseMoveLocation.X));
                barIndex = this.lastSelectedBarIndex;
            }
        }
        // update text for items
        this.UpdateText(barIndex, cashItemSeries);
        // update total size for all items, and get items to draw
        const listToDraw = this.UpdateClientRect(gr, window);
        gr.save();
        gr.beginPath();
        if (this.displayMode === InfoWindowDisplayMode.SeparateWindow) {
            gr.rect(LastBounds.X, LastBounds.Y, LastBounds.Width, LastBounds.Height);
            gr.clip();
        }
        else {
            if (this.mouseLeave) {
                gr.restore();
                return;
            }
            LastBounds.Location = new Point(this.lastMouseMoveLocation.X + this.cursorRectIndent, this.lastMouseMoveLocation.Y + this.cursorRectIndent);
            this.DockingType = InfoWindowDockingType.None;
            const windowRectangle = this.CorrectRectangle;
            // Check for out of screen coverage
            if (this.lastMouseMoveLocation.X + this.cursorRectIndent + LastBounds.Width >= windowRectangle.Right()) {
                LastBounds.X = this.lastMouseMoveLocation.X - this.cursorRectIndent - LastBounds.Width;
                if (this.lastMouseMoveLocation.Y + this.cursorRectIndent + LastBounds.Height + window.PaddingTop >
                    windowRectangle.Bottom()) {
                    LastBounds.Y = this.lastMouseMoveLocation.Y - this.cursorRectIndent - LastBounds.Height;
                }
            }
            if (LastBounds.Bottom() > windowRectangle.Height + window.PaddingTop) {
                LastBounds.Y = windowRectangle.Height + window.PaddingTop - LastBounds.Height;
            }
            if (LastBounds.Right() > windowRectangle.Right()) {
                LastBounds.X = windowRectangle.Right() - LastBounds.Width;
            }
        }
        gr.RoundRectangle(LastBounds, 4, FCurrentBrush);
        let curX = LastBounds.X + this.leftIndent;
        let curY = LastBounds.Y + this.browserOffset + this.edgeMargin;
        // temp rect for drawing item
        const rect = new Rectangle(curX, curY, 0, this.rowHeight);
        const listToDrawLen = listToDraw.length;
        for (let i = 0; i < listToDrawLen; i++) {
            const res = this.DrawItem(gr, listToDraw[i], curX, curY, rect);
            curX = res.curX;
            curY = res.curY;
            if (!this.IsDocked) {
                const colNum = this.undockColumnsNumber;
                const rowNum = Math.ceil(listToDrawLen / colNum);
                const colWidth = LastBounds.Width / colNum; // width of each column
                if ((i + 1) % rowNum === 0) {
                    curX += colWidth;
                    curY = LastBounds.Y + this.browserOffset + this.edgeMargin;
                }
            }
        }
        gr.restore();
    }
    // Draw individual item with two-column layout
    DrawItem(gr, item, curX, curY, rect) {
        var _a;
        const chartWindow = this.window;
        const windowRectangle = this.CorrectRectangle;
        const FCurrentForeBrush = this.FCurrentForeBrush;
        const curLabelWidthMax = this.curLabelWidthMax;
        const FFont = this.FFont;
        const rowHeight = this.rowHeight;
        const xCached = curX;
        rect.Y = curY;
        rect.X = curX;
        rect.Width = item.LabelWidth;
        const CRR = windowRectangle.X + windowRectangle.Width;
        if (rect.X >= CRR) {
            return { curX, curY };
        }
        else if (rect.X + rect.Width > CRR) {
            rect.Width = CRR - rect.X;
        }
        gr.DrawString(item.Label, item.Font, FCurrentForeBrush, rect.X, rect.Y, undefined, undefined);
        curX += this.IsDocked ? item.LabelWidth : curLabelWidthMax;
        rect.X = curX;
        rect.Width = item.ValueWidth;
        if (rect.X >= CRR) {
            return { curX, curY };
        }
        else if (rect.X + rect.Width > CRR) {
            rect.Width = CRR - rect.X;
        }
        gr.DrawString((_a = item.Value) !== null && _a !== void 0 ? _a : TerceraChartInfoWindowRenderer.emptyString, FFont, this.getItemValueForegroundBrush(item), rect.X, rect.Y);
        curX += item.ValueWidth;
        curY += this.IsDocked ? 0 : rowHeight;
        curX = this.IsDocked ? curX : xCached;
        return { curX, curY };
    }
    // items text
    UpdateText(barIndex, cashItemSeries) {
        var _a;
        const realBarIndex = (_a = cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.GetIndex(barIndex)) !== null && _a !== void 0 ? _a : barIndex;
        const emptyBar = realBarIndex < 0 || realBarIndex >= cashItemSeries.Count();
        if (emptyBar)
            barIndex = 0;
        const DictItems = this.DictItems;
        const DictItemsKeys = Object.keys(DictItems);
        const DictItemsLen = DictItemsKeys.length;
        const lastInfoWindowShowingType = this.lastInfoWindowShowingType;
        const DictItemsShowingType = TerceraChartInfoWindowRenderer.DictItemsShowingType;
        const emptyString = TerceraChartInfoWindowRenderer.emptyString;
        const instrument = this.instrument;
        if (!instrument || !cashItemSeries) {
            // force reset text for other instrument
            for (let i = 0; i < DictItemsLen; i++) {
                const it = DictItems[DictItemsKeys[i]];
                it.Value = emptyString;
                it.SetValueWidth(0, true);
            }
            return;
        }
        const period = cashItemSeries.CashItem.FPeriod;
        const dataType = cashItemSeries.settings.DataType;
        const lastShowingTypeArr = DictItemsShowingType[lastInfoWindowShowingType];
        for (let i = 0; i < lastShowingTypeArr.length; i++) {
            const type = lastShowingTypeArr[i];
            if (!DictItems[type].Visible) {
                continue;
            } // пропускаем
            if (emptyBar &&
                type !== InfoWindowItemType.Funding &&
                type !== InfoWindowItemType.Countdown &&
                type !== InfoWindowItemType.DailyChange) {
                DictItems[type].Value = emptyString;
                continue;
            }
            let formatValue = '';
            let value = 0;
            switch (type) {
                case InfoWindowItemType.None:
                    break;
                case InfoWindowItemType.Bars: {
                    const barFromLast = cashItemSeries.Count() - cashItemSeries.rightPaddingBarsCount - 1 - barIndex;
                    DictItems[type].Value = barFromLast.toString();
                    break;
                }
                // FIX.
                case InfoWindowItemType.Date: {
                    let dayTicks;
                    const dayFromBar = cashItemSeries.GetValue(barIndex, BarIndex.Time);
                    if (this.IsBigPeriod(period)) {
                        dayTicks = Periods.ConvertToDisplayedTimeForDayAggregations(new Date(dayFromBar), instrument, period);
                    }
                    else {
                        dayTicks = DateTimeConvertor.getLocalTimeQuick(dayFromBar);
                    }
                    DictItems[type].Value = DateTimeUtils.formatDate(dayTicks, 'DD.MM.YYYY');
                    break;
                }
                // FIX.
                case InfoWindowItemType.Time:
                    if (this.IsBigPeriod(period)) {
                        DictItems[type].Value = StringUtils.THREE_DASHES;
                    }
                    else {
                        const ticks = DateTimeConvertor.getLocalTimeQuick(cashItemSeries.GetValue(barIndex, BarIndex.Time));
                        DictItems[type].Value = DateTimeUtils.formatDate(ticks, 'HH:mm:ss');
                    }
                    break;
                case InfoWindowItemType.Open:
                case InfoWindowItemType.Close:
                case InfoWindowItemType.High:
                case InfoWindowItemType.Low:
                case InfoWindowItemType.HighLow: {
                    // Get value
                    switch (type) {
                        case InfoWindowItemType.Open:
                            value = cashItemSeries.GetValue(barIndex, BarIndex.Open);
                            break;
                        case InfoWindowItemType.Close:
                            value = cashItemSeries.GetValue(barIndex, BarIndex.Close);
                            break;
                        case InfoWindowItemType.High:
                            value = cashItemSeries.GetValue(barIndex, BarIndex.High);
                            break;
                        case InfoWindowItemType.Low:
                            value = cashItemSeries.GetValue(barIndex, BarIndex.Low);
                            break;
                        case InfoWindowItemType.HighLow:
                            value =
                                cashItemSeries.GetValue(barIndex, BarIndex.High) -
                                    cashItemSeries.GetValue(barIndex, BarIndex.Low);
                            break;
                    }
                    // Specific format depends on DataType
                    if (!isNullOrUndefined(instrument)) {
                        formatValue = cashItemSeries.settings.FormatPriceConverter(value, instrument);
                    }
                    DictItems[type].Value = formatValue;
                    break;
                }
                case InfoWindowItemType.DailyChange: {
                    DictItems[type].NumberValue = instrument.Level1.GetChangePercent(this.Account);
                    DictItems[type].Value = instrument.Level1.strChangePercent(this.Account);
                    break;
                }
                case InfoWindowItemType.Change: {
                    let open = cashItemSeries.GetValue(barIndex, BarIndex.Open);
                    let close = cashItemSeries.GetValue(barIndex, BarIndex.Close);
                    if (dataType === TerceraChartCashItemSeriesDataType.Log) {
                        open = cashItemSeries.settings.logDataConverter.Revert(open);
                        close = cashItemSeries.settings.logDataConverter.Revert(close);
                    }
                    const change = /* Math.round( */ ((close - open) / Math.abs(open)) * 100;
                    let changeStr;
                    if (open === 0) {
                        changeStr = emptyString;
                    }
                    else {
                        // http://tp.pfsoft.net/entity/55604
                        if (change === 0) {
                            changeStr = (0).toFixed(2);
                        }
                        else {
                            changeStr = change.toFixed(2);
                        }
                    }
                    DictItems[type].NumberValue = change;
                    DictItems[type].Value = changeStr;
                    break;
                }
                case InfoWindowItemType.Volume:
                case InfoWindowItemType.Ticks: {
                    const obj = cashItemSeries.CashItem.GetVolumeAndTicks(cashItemSeries.GetIndex(barIndex));
                    const volume = obj.volume;
                    const ticks = obj.ticks;
                    if (type === InfoWindowItemType.Volume) {
                        DictItems[type].Value = volume === null ? '--' : VolumeFormatter.getVolumeFormat(volume);
                    }
                    else if (type === InfoWindowItemType.Ticks) {
                        DictItems[type].Value = ticks === null ? '--' : ticks.toString();
                    }
                    break;
                }
                case InfoWindowItemType.Ask:
                    DictItems[type].Value = instrument.formatPrice(cashItemSeries.GetValue(barIndex, 1));
                    break;
                case InfoWindowItemType.Bid:
                    DictItems[type].Value = instrument.formatPrice(cashItemSeries.GetValue(barIndex, 2));
                    break;
                case InfoWindowItemType.PortfolioGrowth:
                    DictItems[type].Value = this.Account.formatPrice(cashItemSeries.GetValue(barIndex, BarIndex.Close));
                    break;
                case InfoWindowItemType.Funding:
                    DictItems[type].Value = instrument.Level1.GetSpreadedFundingRateValueFormatted(this.Account, StringUtils.THREE_DASHES);
                    break;
                case InfoWindowItemType.Countdown:
                    DictItems[type].Value = instrument.Level1.StrNextFundingSettlement(StringUtils.THREE_DASHES);
                    break;
            }
        }
    }
    IsBigPeriod(period) {
        return period !== 0 && (period % Periods.YEAR === 0 || period % Periods.DAY === 0);
    }
    // total width, height
    UpdateClientRect(gr, window) {
        const leftIndent = this.leftIndent;
        const rightIndent = this.rightIndent;
        // добавляем в список на рисование и обмериваем
        const listToDraw = [];
        this.curLabelWidthMax = this.curValueWidthMax = 10;
        this.curTotalWidth = 0;
        const lastShowingTypeArr = TerceraChartInfoWindowRenderer.DictItemsShowingType[this.lastInfoWindowShowingType];
        for (let i = 0; i < lastShowingTypeArr.length; i++) {
            const it = this.DictItems[lastShowingTypeArr[i]];
            if (!it.Visible || !it.Allowed)
                continue;
            listToDraw.push(it);
            const labWidth = it.SetLabelWidth(gr.GetTextWidth(it.Label, it.Font) + leftIndent);
            let valWidth = gr.GetTextWidth(it.Value, it.Font) + leftIndent + rightIndent;
            valWidth = it.SetValueWidth(valWidth);
            this.curLabelWidthMax = Math.max(this.curLabelWidthMax, labWidth);
            this.curValueWidthMax = Math.max(this.curValueWidthMax, valWidth);
            if (this.IsDocked) {
                this.curTotalWidth += labWidth + valWidth;
            }
        }
        const CR = this.CorrectRectangle;
        const CRX = CR.X;
        const CRW = CR.Width;
        const CRH = CR.Height;
        const CRB = CR.Y + CRH;
        const LBR = this.LastBounds;
        if (this.IsDocked) {
            LBR.Height = this.rowHeight;
            LBR.Width = Math.min(CRW, this.curTotalWidth);
            if (this.IsDockedTop) {
                LBR.Location = CR.Location;
            }
            else {
                LBR.X = CRX;
                LBR.Y = CRB - this.rowHeight;
            }
        }
        else {
            const rowsNumber = Math.ceil(listToDraw.length / this.undockColumnsNumber);
            this.curTotalWidth = this.GetUndockedWidth;
            LBR.Height = rowsNumber * this.rowHeight;
            LBR.Width = this.curTotalWidth;
            // Adjust position if out of view in undocked mode
            if (LBR.Y + LBR.Height > CRB) {
                LBR.X = CRX;
                if (CRH >= LBR.Height) {
                    LBR.Y = CRB - LBR.Height;
                }
                else {
                    this.DockingType = InfoWindowDockingType.Top; // Change to docked if height exceeds available space
                }
            }
            if (LBR.X < CRX) {
                LBR.X = CRX;
            }
        }
        this.lastDrawCount = listToDraw.length;
        return listToDraw;
    }
    // #region Process mouse events
    ProcessMouseDown(e) {
        if (this.isHide() || e.Button !== MouseButtons.Left) {
            return super.ProcessMouseDown(e);
        }
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        if (this.LastBounds.Contains(e.Location.X, e.Location.Y)) {
            this.lastMouseMoveLocation = e.Location;
            this.mouseState = TerceraChartPriceScaleRendererMouseState.Moving;
            this.chart.UpdateCursor();
            this.chart.IsDirty();
            return true;
        }
        else {
            this.IsMouseDownOut = true;
        }
        return super.ProcessMouseDown(e);
    }
    isMoving() {
        return this.mouseState === TerceraChartPriceScaleRendererMouseState.Moving;
    }
    ProcessMouseMove(e) {
        if (this.isHide() || isNullOrUndefined(this.window)) {
            return false;
        }
        const eLocation = e.Location;
        const eLocationX = eLocation.X;
        const eLocationY = eLocation.Y;
        const CR = this.CorrectRectangle;
        const CRX = CR.X;
        const CRY = CR.Y;
        const CRW = CR.Width;
        const CRH = CR.Height;
        const CRR = CRX + CRW;
        const CRB = CRY + CRH;
        const LBR = this.LastBounds;
        let res = false;
        if (this.isMoving()) {
            const newX = this.lastMouseMoveLocation.X;
            const xDeltaPx = eLocationX - newX;
            LBR.X += xDeltaPx;
            const newY = this.lastMouseMoveLocation.Y;
            const yDeltaPy = eLocationY - newY;
            LBR.Y += yDeltaPy;
            const unDockedWidth = this.GetUndockedWidth;
            const unDockedHeight = this.lastDrawCount * this.rowHeight;
            //
            // Y
            //
            // special case - not enought heigth to undock
            if (yDeltaPy > 0 && this.IsDockedTop && unDockedHeight >= CRH) {
                this.DockingType = InfoWindowDockingType.Bottom;
            }
            else if (yDeltaPy < 0 && this.IsDockedBottom && unDockedHeight >= CRH) {
                this.DockingType = InfoWindowDockingType.Top;
            }
            else if (eLocationY < CRY || LBR.Y < CRY) {
                // more top
                this.DockingType = InfoWindowDockingType.Top;
                LBR.Location = CR.Location;
            }
            else if (((yDeltaPy > 0 || eLocationY > CRB - this.rowHeight) && this.IsDockedBottom) ||
                LBR.Y + LBR.Height > CRB) {
                // less bottom
                this.DockingType = InfoWindowDockingType.Bottom;
                LBR.X = CRX;
                LBR.Y = CRB - this.rowHeight;
            }
            else if ((this.IsDockedTop && LBR.Y > CRY) ||
                (!this.isMouseIn(eLocationX, eLocationY) && CR.Contains(eLocationX, eLocationY) && this.mouseLeave)) {
                // на андок и на вход обратно в пределы нычанаем тягать за левый угол
                this.DockingType = InfoWindowDockingType.None;
                LBR.Location = this.lastMouseMoveLocation = eLocation;
            }
            else if (this.IsDockedBottom && yDeltaPy < 0) {
                // на андок и на вход обратно в пределы нычанаем тягать за левый нижний угол
                this.DockingType = InfoWindowDockingType.None;
                this.lastMouseMoveLocation = eLocation;
                LBR.Location = new Point(this.lastMouseMoveLocation.X, this.lastMouseMoveLocation.Y - unDockedHeight);
            }
            else if (this.IsDocked) {
                /* X */
                LBR.X = CRX;
            }
            else if (eLocationX < CRX || LBR.X < CRX) {
                /* out left edge */
                LBR.X = CRX;
            }
            else if (eLocationX > CRR - unDockedWidth) {
                /* out right edge */
                LBR.X = CRR - unDockedWidth;
            }
            res = true;
        }
        this.lastMouseMoveLocation = eLocation;
        //
        // при тягании покунули пределы экрана
        //
        if (!CR.Contains(eLocationX, eLocationY) && this.isMoving()) {
            this.ProcessMouseLeave(e);
        }
        else {
            this.mouseLeave = false;
        }
        if (this.isMouseIn(eLocationX, eLocationY)) {
            e.NeedRedraw |= LayersEnum.CrossHair;
        }
        if (!res) {
            return super.ProcessMouseMove(e);
        }
        else {
            return true;
        }
    }
    ProcessMouseUp(e) {
        if (this.isHide() || e.Button !== MouseButtons.Left) {
            return super.ProcessMouseDown(e);
        }
        this.mouseState = TerceraChartPriceScaleRendererMouseState.None;
        this.IsMouseDownOut = false;
        return super.ProcessMouseUp(e);
    }
    ProcessMouseLeave(e) {
        super.ProcessMouseLeave(e);
        this.mouseLeave = true;
    }
    // #endregion
    // #region overriden
    Localize() {
        const DictItems = this.DictItems;
        DictItems[InfoWindowItemType.Bars].Label = Resources.getResource('chart.infoWindow.Items.Bars');
        DictItems[InfoWindowItemType.Date].Label = Resources.getResource('chart.infoWindow.Items.Date');
        DictItems[InfoWindowItemType.Time].Label = Resources.getResource('chart.infoWindow.Items.Time');
        DictItems[InfoWindowItemType.Open].Label = Resources.getResource('chart.infoWindow.Items.Open');
        DictItems[InfoWindowItemType.High].Label = Resources.getResource('chart.infoWindow.Items.High');
        DictItems[InfoWindowItemType.Low].Label = Resources.getResource('chart.infoWindow.Items.Low');
        DictItems[InfoWindowItemType.Close].Label = Resources.getResource('chart.infoWindow.Items.Close');
        DictItems[InfoWindowItemType.Change].Label = Resources.getResource('chart.infoWindow.Items.Change');
        DictItems[InfoWindowItemType.DailyChange].Label = Resources.getResource('chart.infoWindow.Items.DailyChange');
        DictItems[InfoWindowItemType.Ticks].Label = Resources.getResource('chart.infoWindow.Items.Ticks');
        DictItems[InfoWindowItemType.Volume].Label = Resources.getResource('chart.infoWindow.Items.Volume');
        DictItems[InfoWindowItemType.Bid].Label = Resources.getResource('chart.infoWindow.Items.Bid');
        DictItems[InfoWindowItemType.Ask].Label = Resources.getResource('chart.infoWindow.Items.Ask');
        DictItems[InfoWindowItemType.PortfolioGrowth].Label = Resources.getResource('chart.infoWindow.Items.PortfolioGrowth');
        DictItems[InfoWindowItemType.Funding].Label = Resources.getResource('chart.infoWindow.Items.Funding');
        DictItems[InfoWindowItemType.Countdown].Label = Resources.getResource('chart.infoWindow.Items.Countdown');
        DictItems[InfoWindowItemType.Ticks].Visible = !Resources.isHidden(DictItems[InfoWindowItemType.Ticks].Label);
        DictItems[InfoWindowItemType.Volume].Visible = !Resources.isHidden(DictItems[InfoWindowItemType.Volume].Label);
        const highLabel = DictItems[InfoWindowItemType.High].Label; // дабы не добавлять переводы во все языки для HighLow совмещаем локализации High и Low
        const highPart = highLabel.substr(0, highLabel.length - 1); // отрезаем двоеточие в конце ключа High
        DictItems[InfoWindowItemType.HighLow].Label = highPart + ' - ' + DictItems[InfoWindowItemType.Low].Label;
        if (this.shortMode) {
            const keys = Object.keys(DictItems);
            const len = keys.length;
            for (let i = 0; i < len; i++) {
                const item = DictItems[keys[i]];
                item.Label = item.Label.charAt(0) + ':';
            }
            DictItems[InfoWindowItemType.HighLow].Label =
                DictItems[InfoWindowItemType.High].Label.charAt(0) +
                    '-' +
                    DictItems[InfoWindowItemType.Low].Label.charAt(0) +
                    ':';
            DictItems[InfoWindowItemType.DailyChange].Label =
                Resources.getResource('chart.infoWindow.Items.DailyChangeShort') + ':';
        }
    }
    ThemeChanged() {
        const currentTheme = ThemeManager.colors;
        this.BackColor = currentTheme.chartInfoWindowBackColor;
        this.ForeColor = currentTheme.chartInfoWindowTitleForeground;
        this.NeutralValueColor = currentTheme.neutralColor;
        this.IncreaseValueColor = currentTheme.positiveColor;
        this.DecreaseValueColor = currentTheme.negativeColor;
    }
    GetCursor(e) {
        if (this.isMouseIn(e.X, e.Y) && this.Visible && this.displayMode === InfoWindowDisplayMode.SeparateWindow) {
            return this.isMoving() ? Cursors.Grabbing : Cursors.Grab;
        }
        return super.GetCursor(e);
    }
    GetContextMenu(e, chart) {
        if (this.isHide() || !this.isMouseIn(e.X, e.Y)) {
            return super.GetContextMenu(e, chart);
        }
        return [
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowMode, InfoWindowDisplayMode.AttachedToCursor)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowHide, this)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowDock, this)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowShortMode, this)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.InfoWindowSettings, this))
        ];
    }
    // #endregion
    // #region ICaller
    callBack(properties) {
        let p = DynProperty.getPropertyByName(properties, 'infoWindow_fore_back_color');
        if (p) {
            const pairColor = p.value;
            this.ForeColor = pairColor.Color1;
            this.BackColor = pairColor.Color2;
        }
        p = DynProperty.getPropertyByName(properties, 'infoWindow_value_neutral_color');
        if (p != null) {
            this.NeutralValueColor = p.value;
        }
        p = DynProperty.getPropertyByName(properties, 'infoWindow_value_increase_color');
        if (p != null) {
            this.IncreaseValueColor = p.value;
        }
        p = DynProperty.getPropertyByName(properties, 'infoWindow_value_decrease_color');
        if (p != null) {
            this.DecreaseValueColor = p.value;
        }
        p = DynProperty.getPropertyByName(properties, 'infowindow_displayMode');
        if (!isNullOrUndefined(p))
            this.displayMode = p.value;
        p = DynProperty.getPropertyByName(properties, 'infowindow_visible'); // infowindow_visible is no longer saved
        if (!isNullOrUndefined(p))
            this.displayMode = p.value ? InfoWindowDisplayMode.SeparateWindow : InfoWindowDisplayMode.Hidden;
        p = DynProperty.getPropertyByName(properties, 'infowindow_Docked');
        if (!isNullOrUndefined(p))
            this.DockingType = p.value;
        let temp = DynProperty.getPropertyByName(properties, 'infowindow_fx');
        if (!isNullOrUndefined(temp))
            this.LastBounds.X = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'infowindow_fy');
        if (!isNullOrUndefined(temp))
            this.LastBounds.Y = temp.value;
        // Шрифт
        temp = DynProperty.getPropertyByName(properties, 'infowindow_font');
        if (!isNullOrUndefined(temp))
            this.Font = temp.value;
        // Visibility
        temp = DynProperty.getPropertyByName(properties, 'IsBarsVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Bars].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsDateVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Date].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsTimeVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Time].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsOpenVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Open].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsCloseVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Close].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsHighVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.High].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsLowVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Low].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsHighLowVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.HighLow].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsChangeVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Change].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsDailyChangeVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.DailyChange].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsTicksVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Ticks].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsVolumeVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Volume].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsCountdownVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Countdown].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsFundingVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.Funding].Visible = temp.value;
        temp = DynProperty.getPropertyByName(properties, 'IsPortfolioGrowthVisible');
        if (!isNullOrUndefined(temp))
            this.DictItems[InfoWindowItemType.PortfolioGrowth].Visible = temp.value;
        // force reset text for other instrument
        const DictItems = this.DictItems;
        const keys = Object.keys(this.DictItems);
        const len = keys.length;
        for (let i = 0; i < len; i++) {
            const it = DictItems[keys[i]];
            it.Value = TerceraChartInfoWindowRenderer.emptyString;
            it.SetValueWidth(0, true);
        }
    }
    Properties() {
        let properties = [];
        let prop = null;
        // #region hidden
        // properties.push(new DynProperty('oldColor', this.ForeColor, DynProperty.COLOR, DynProperty.HIDDEN_GROUP)); // seems to be deprecated
        properties.push(new DynProperty('infowindow_Docked', this.DockingType, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('infowindow_fx', this.LastBounds.X, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('infowindow_fy', this.LastBounds.Y, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
        // #endregion
        // visibility
        let SeparatorGroup = '#0#' + Resources.getResource('property.VisibilitySeparatorGroup');
        prop = new DynProperty('infowindow_displayMode', this.displayMode, DynProperty.COMBOBOX, DynProperty.DATA_BOX_GROUP);
        prop.sortIndex = 0;
        prop.objectVariants = createContextMenuItemsFromEnum(InfoWindowDisplayMode, getInfoWindowDisplayModeKey);
        prop.separatorGroup = SeparatorGroup;
        prop.assignedProperty = [
            'IsBarsVisible',
            'IsDateVisible',
            'IsTimeVisible',
            'IsOpenVisible',
            'IsCloseVisible',
            'IsHighVisible',
            'IsLowVisible',
            'IsHighLowVisible',
            'IsChangeVisible',
            'IsDailyChangeVisible',
            'IsTicksVisible',
            'IsVolumeVisible',
            'IsPortfolioGrowthVisible',
            'IsCountdownVisible',
            'IsFundingVisible',
            'infowindow_font'
            // 'infoWindow_fore_back_color' // <- reason of bug #124317
        ];
        properties.push(prop);
        // View
        properties = properties.concat(this.getViewProperties(DynProperty.DATA_BOX_GROUP, '#1#' + Resources.getResource('property.ViewSeparatorGroup')));
        // Colors
        SeparatorGroup = '#2#' + Resources.getResource('property.ColorsSeparatorGroup');
        /* if (FFont)
    {
        // Шрифт
        prop = new DynProperty("infowindow_font", FFont.copy(), DynProperty.FONT, DynProperty.DATA_BOX_GROUP);
        prop.sortIndex = 0;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = Visible;
        properties.push(prop);
    } */
        prop = new DynProperty('infoWindow_fore_back_color', new PairColor(this.ForeColor, this.BackColor, Resources.getResource('propery.MainPriceRenderer.Text'), Resources.getResource('propery.MainPriceRenderer.Back')), DynProperty.PAIR_COLOR, DynProperty.DATA_BOX_GROUP);
        prop.sortIndex = 1;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = this.Visible;
        properties.push(prop);
        prop = new DynProperty('infoWindow_value_neutral_color', this.NeutralValueColor, DynProperty.COLOR, DynProperty.DATA_BOX_GROUP);
        prop.sortIndex = 2;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = this.Visible;
        properties.push(prop);
        prop = new DynProperty('infoWindow_value_increase_color', this.IncreaseValueColor, DynProperty.COLOR, DynProperty.DATA_BOX_GROUP);
        prop.sortIndex = 3;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = this.Visible;
        properties.push(prop);
        prop = new DynProperty('infoWindow_value_decrease_color', this.DecreaseValueColor, DynProperty.COLOR, DynProperty.DATA_BOX_GROUP);
        prop.sortIndex = 4;
        prop.separatorGroup = SeparatorGroup;
        prop.enabled = this.Visible;
        properties.push(prop);
        return properties;
    }
    getViewProperties(group, separatorGroup) {
        const properties = [];
        const dictItems = this.DictItems;
        let sortIndex = 1;
        for (const key in dictItems) {
            const item = dictItems[key];
            if (!item.Allowed ||
                key === InfoWindowItemType.Ask.toString() ||
                key === InfoWindowItemType.Bid.toString()) {
                continue;
            }
            const prop = new DynProperty('Is' + TerceraChartInfoWindowRenderer.infoWindowItemTypeToString(key) + 'Visible', dictItems[key].Visible, DynProperty.BOOLEAN, group);
            prop.sortIndex = sortIndex++;
            prop.separatorGroup = separatorGroup;
            prop.enabilityValue = [InfoWindowDisplayMode.SeparateWindow, InfoWindowDisplayMode.AttachedToCursor];
            prop.enabled = this.Visible && this.displayMode !== InfoWindowDisplayMode.Hidden;
            properties.push(prop);
        }
        return properties;
    }
    // #endregion
    // Once it should be located in TerceraChartBaseInfoWindowRenderer and changed here as protected override ig
    getItemValueForegroundBrush(item) {
        let brush = this.FCurrentForeBrush;
        if ((item.id !== InfoWindowItemType.Change && item.id !== InfoWindowItemType.DailyChange) ||
            item.NumberValue === 0) {
            brush = this.FNeutralValueBrush;
        }
        else {
            brush = item.NumberValue < 0 ? this.FDecreaseValueBrush : this.FIncreaseValueBrush;
        }
        return brush;
    }
    isHide() {
        return !this.Visible || this.displayMode === InfoWindowDisplayMode.Hidden;
    }
    isMouseIn(x, y) {
        return this.Visible && this.LastBounds.Contains(x, y);
    }
    static infoWindowItemTypeToString(type) {
        type = parseInt(type);
        if (isNaN(type))
            return '';
        switch (type) {
            case InfoWindowItemType.Bars:
                return 'Bars';
            case InfoWindowItemType.Date:
                return 'Date';
            case InfoWindowItemType.Time:
                return 'Time';
            case InfoWindowItemType.Open:
                return 'Open';
            case InfoWindowItemType.High:
                return 'High';
            case InfoWindowItemType.Low:
                return 'Low';
            case InfoWindowItemType.HighLow:
                return 'HighLow';
            case InfoWindowItemType.Close:
                return 'Close';
            case InfoWindowItemType.Change:
                return 'Change';
            case InfoWindowItemType.DailyChange:
                return 'DailyChange';
            case InfoWindowItemType.Ticks:
                return 'Ticks';
            case InfoWindowItemType.Volume:
                return 'Volume';
            case InfoWindowItemType.Bid:
                return 'Bid';
            case InfoWindowItemType.Ask:
                return 'Ask';
            case InfoWindowItemType.PortfolioGrowth:
                return 'PortfolioGrowth';
            case InfoWindowItemType.Funding:
                return 'Funding';
            case InfoWindowItemType.Countdown:
                return 'Countdown';
        }
        return '';
    }
}
TerceraChartInfoWindowRenderer.emptyString = StringUtils.THREE_DASHES;
// --------------------------
// Возможные комбинация отображения групп item'ов
TerceraChartInfoWindowRenderer.DictItemsShowingType = {
    [InfoWindowShowingType.IntradayAbsoluteChart]: [
        InfoWindowItemType.Bars,
        InfoWindowItemType.Date,
        InfoWindowItemType.Time,
        InfoWindowItemType.Open,
        InfoWindowItemType.High,
        InfoWindowItemType.Low,
        InfoWindowItemType.Close,
        InfoWindowItemType.HighLow,
        InfoWindowItemType.Change,
        InfoWindowItemType.DailyChange,
        InfoWindowItemType.Ticks,
        InfoWindowItemType.Volume,
        InfoWindowItemType.Funding,
        InfoWindowItemType.Countdown
    ],
    [InfoWindowShowingType.TicksAbsoluteChart]: [
        InfoWindowItemType.Bars,
        InfoWindowItemType.Date,
        InfoWindowItemType.Time,
        InfoWindowItemType.Ask,
        InfoWindowItemType.Bid
    ],
    [InfoWindowShowingType.PortfolioGrowth]: [InfoWindowItemType.Date, InfoWindowItemType.PortfolioGrowth],
    [InfoWindowShowingType.None]: []
};
